import { cloneDeep, keyBy } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { excludedAllTasksFields } from 'components/organisms/tasks/constants';
import { FilterableField } from 'components/organisms/tasks/fields';
import { excludedTaskFields } from 'components/pages/task-list/task-list';
import { RootState } from 'data';
import { useAccount } from 'hooks/use-account';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { FieldsStore, TaskField } from 'stores/fields';
import { withValidCustomTaskFields } from 'utils/duplicate-fields-transformer';

const EMPTY_RECORD = {};

export const useTaskFields = () => {
  const { data: jiraIntegrationConfig } = useQueryJiraConfig();
  const { pathname } = useLocation();

  const isProject = pathname.includes('projects');

  const excludedFields = useMemo(() => {
    // remove jiraIntegration from excluded fields ONLY when the integration is active
    if (jiraIntegrationConfig?.length === 0) {
      return [
        ...(isProject ? excludedTaskFields : excludedAllTasksFields),
        'jiraIntegration',
      ];
    }

    return isProject ? excludedTaskFields : excludedAllTasksFields;
  }, [jiraIntegrationConfig, isProject]);

  const excludedFieldsForColumns = useMemo(
    () => [
      ...excludedFields,
      'private',
      'completedLateBy',
      'phaseName',
      'taskDependencyStatus',
    ],
    [excludedFields],
  );

  const excludedFieldsForFilters = useMemo(
    () => [
      ...excludedFields,
      'startDateActual',
      isProject ? 'phaseName' : 'projectPhaseId',
    ],
    [excludedFields, isProject],
  );

  const { accountId } = useAccount();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);

  const { fields, fieldsForFilter } = useMemo(() => {
    const taskFields = FieldsStore.enabledTaskFields(fieldsStore).map((field) =>
      withValidCustomTaskFields(accountId, cloneDeep(field)),
    );

    const filteredFields = taskFields.reduce(
      (acc, field) => {
        const columns = !excludedFieldsForColumns.includes(field.fieldName);
        const filters = !excludedFieldsForFilters.includes(field.fieldName);

        if (columns) {
          acc.fields.push(field);
        }
        if (filters) {
          acc.fieldsForFilter.push(field);
        }

        return acc;
      },
      {
        fields: [] as FilterableField[],
        fieldsForFilter: [] as FilterableField[],
      },
    );

    return filteredFields;
  }, [
    fieldsStore,
    accountId,
    excludedFieldsForColumns,
    excludedFieldsForFilters,
  ]);

  const taskFieldsLookupById = useMemo(
    () => keyBy(fields, 'fieldId') as Record<number, TaskField>,
    [fields],
  );

  const taskFilterFieldsLookupByName: Record<string, FilterableField> = useMemo(
    () =>
      fieldsForFilter.length > 0
        ? (keyBy(fieldsForFilter, 'fieldName') as Record<string, TaskField>)
        : EMPTY_RECORD,
    [fieldsForFilter],
  );

  const fieldsLookupByName: Record<string, FilterableField> = useMemo(
    () =>
      fields.length > 0
        ? (keyBy(fields, 'fieldName') as Record<string, FilterableField>)
        : EMPTY_RECORD,
    [fields],
  );

  return {
    fields,
    fieldsForFilter,
    taskFieldsLookupById,
    taskFilterFieldsLookupByName,
    fieldsLookupByName,
  };
};
